import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Reply, ThumbsUp, ThumbsDown, Flag, Trash2, AlertTriangle, MessageSquare, ArrowDown, ArrowUp, MoveRight, ArrowRight, X, User, Settings, Camera } from 'lucide-react';
import { useAuth } from '../../lib/auth-context'; 
import { budgetCountries } from '../../data/budget-countries';
import { doc, updateDoc, arrayUnion, arrayRemove, deleteDoc, getDocs, collection, query, where, writeBatch, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface Message {
  id: string;
  content: string;
  user_id: string;
  user_email: string;
  username: string;
  created_at: { toDate: () => Date };
  likes: string[];
  dislikes: string[];
  thread_code?: string;
  thread_type?: 'country' | 'general' | 'bug';
  reply_to?: {
    id: string;
    user_email: string;
    username: string;
    user_id?: string;
  };
  is_admin?: boolean;
}

interface MessageListProps {
  messages: Message[];
  onReply: (message: Message) => void;
}

const formatDate = (date: Date) => {
  return formatDistanceToNow(date, { addSuffix: true, locale: fr });
};

export function MessageList({ messages, onReply }: MessageListProps) {
  const { user, userInfo, isAdmin } = useAuth();
  const [messageToDelete, setMessageToDelete] = React.useState<Message | null>(null);
  const [sortNewestFirst, setSortNewestFirst] = React.useState(() => {
    const savedSort = localStorage.getItem('messageSort');
    return savedSort === null ? false : savedSort === 'newest';
  });
  const messagesStartRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [prevMessagesLength, setPrevMessagesLength] = React.useState(0);
  const [displayLimit, setDisplayLimit] = React.useState(15);
  const [showMoveModal, setShowMoveModal] = React.useState(false);
  const [messageToMove, setMessageToMove] = React.useState<Message | null>(null);
  const [targetThread, setTargetThread] = React.useState<string>('');
  const [availableThreads, setAvailableThreads] = React.useState<{code: string, name: string, type: 'country' | 'general' | 'bug'}[]>([
    { code: 'general_discussion', name: 'Discussion générale', type: 'general' },
    { code: 'bug_report', name: 'Signalements de bugs', type: 'bug' },
    ...budgetCountries.map(country => ({
      code: country.code,
      name: country.name,
      type: 'country' as const
    }))
  ]);
  const [isMoving, setIsMoving] = React.useState(false);
  const [replyToMessage, setReplyToMessage] = React.useState<Message | null>(null);

  React.useEffect(() => {
    localStorage.setItem('messageSort', sortNewestFirst ? 'newest' : 'oldest');
  }, [sortNewestFirst]);

  React.useEffect(() => {
    if (messages.length > prevMessagesLength && scrollContainerRef.current) {
      const previousScrollHeight = scrollContainerRef.current.scrollHeight;
      const previousScrollTop = scrollContainerRef.current.scrollTop;
      
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const newScrollHeight = scrollContainerRef.current.scrollHeight;
          const heightDifference = newScrollHeight - previousScrollHeight;
          scrollContainerRef.current.scrollTop = previousScrollTop + heightDifference;
        }
      }, 0);
    }
    
    setPrevMessagesLength(messages.length);
  }, [messages, prevMessagesLength]);

  const buildMessageTree = React.useMemo(() => {
    const messageMap = new Map<string, Message[]>();
    
    messages.forEach(message => {
      if (message.reply_to) {
        const parentId = message.reply_to.id;
        if (!messageMap.has(parentId)) {
          messageMap.set(parentId, []);
        }
        messageMap.get(parentId)?.push(message);
      }
    });
    
    messageMap.forEach(replies => {
      replies.sort((a, b) => {
        return a.created_at.toDate().getTime() - b.created_at.toDate().getTime();
      });
    });
    
    return messageMap;
  }, [messages]);

  const topLevelMessages = React.useMemo(() => {
    return messages
      .filter(message => !message.reply_to)
      .sort((a, b) => {
        const aTime = a.created_at.toDate().getTime();
        const bTime = b.created_at.toDate().getTime();
        return sortNewestFirst ? bTime - aTime : aTime - bTime;
      });
  }, [messages, sortNewestFirst]);

  const visibleMessages = React.useMemo(() => {
    return topLevelMessages.slice(-displayLimit);
  }, [topLevelMessages, displayLimit]);

  const hasMoreMessages = topLevelMessages.length > displayLimit;

  const loadMoreMessages = () => {
    setDisplayLimit(prev => prev + 15);
  };

  const handleReaction = async (messageId: string, isLike: boolean) => {
    if (!user) return;

    try {
      const messageRef = doc(db, 'messages', messageId);
      const message = messages.find(m => m.id === messageId);
      if (!message) return;

      if (message.user_id === user.uid) return;

      const hasLiked = (message.likes || []).includes(user.uid);
      const hasDisliked = (message.dislikes || []).includes(user.uid);

      if (isLike) {
        if (hasDisliked) {
          await updateDoc(messageRef, {
            dislikes: arrayRemove(user.uid)
          });
        }
        await updateDoc(messageRef, {
          likes: hasLiked ? arrayRemove(user.uid) : arrayUnion(user.uid)
        });
      } else {
        if (hasLiked) {
          await updateDoc(messageRef, {
            likes: arrayRemove(user.uid)
          });
        }
        await updateDoc(messageRef, {
          dislikes: hasDisliked ? arrayRemove(user.uid) : arrayUnion(user.uid)
        });
      }
    } catch (error) {
      console.error('Error updating reaction:', error);
    }
  };

  const handleReport = async (messageId: string) => {
    if (!user) return;

    try {
      const messageRef = doc(db, 'messages', messageId);
      await updateDoc(messageRef, {
        reported: true,
        reported_by: user.uid,
        reported_at: new Date()
      });

      alert('Message signalé aux modérateurs.');
    } catch (error) {
      console.error('Error reporting message:', error);
    }
  };

  const handleDelete = async () => {
    if (!messageToDelete || !isAdmin) return;

    try {
      await deleteDoc(doc(db, 'messages', messageToDelete.id));
      setMessageToDelete(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Erreur lors de la suppression du message');
    }
  };

  const handleMoveMessage = (message: Message) => {
    setMessageToMove(message);
    setTargetThread('');
    setShowMoveModal(true);
  };

  const renderMessageWithReplies = (message: Message, level: number = 0) => {
    const replies = buildMessageTree.get(message.id) || [];
    
    return (
      <React.Fragment key={message.id}>
        <MessageComponent
          message={message}
          isReply={level > 0}
          replyToMessage={replyToMessage}
          isAdmin={isAdmin}
          userInfo={userInfo}
          user={user}
          onDelete={() => setMessageToDelete(message)}
          onReply={() => handleReply(message)}
          onReaction={handleReaction}
          onReport={handleReport}
          onMove={() => handleMoveMessage(message)}
        />
        {replies.length > 0 && (
          <div className={`ml-${Math.min(8, level + 4)}`}>
            {replies.map(reply => renderMessageWithReplies(reply, level + 1))}
          </div>
        )}
      </React.Fragment>
    );
  };

  const handleReply = (message: Message) => {
    setReplyToMessage(message);
    
    onReply({
      id: message.id,
      content: message.content,
      user_id: message.user_id,
      user_email: message.user_email,
      username: message.username,
      created_at: message.created_at,
      likes: message.likes,
      dislikes: message.dislikes
    });
  };

  const executeMove = async () => {
    if (!messageToMove || !targetThread || isMoving) return;
    
    setIsMoving(true);
    try {
      const batch = writeBatch(db);
      const messageRef = doc(db, 'messages', messageToMove.id);
      
      const targetThreadType = availableThreads.find(t => t.code === targetThread)?.type || 'country';
      batch.update(messageRef, {
        thread_code: targetThread,
        thread_type: targetThreadType
      });
      
      const directRepliesQuery = query(
        collection(db, 'messages'),
        where('reply_to.id', '==', messageToMove.id)
      );
      const directRepliesSnapshot = await getDocs(directRepliesQuery);
      
      directRepliesSnapshot.forEach(replyDoc => {
        batch.update(doc(db, 'messages', replyDoc.id), {
          thread_code: targetThread,
          thread_type: targetThreadType
        });
      });
      
      await batch.commit();
      
      setShowMoveModal(false);
      setMessageToMove(null);
      setTargetThread('');
      
      alert(`Message déplacé avec succès vers ${availableThreads.find(t => t.code === targetThread)?.name}`);
    } catch (error) {
      console.error('Erreur lors du déplacement du message:', error);
      alert('Erreur lors du déplacement du message');
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <div ref={scrollContainerRef} className="flex flex-col p-4 space-y-4 custom-scrollbar">
      <div ref={messagesStartRef} />
      
      {hasMoreMessages && (
        <div className="flex justify-center my-4">
          <button
            onClick={loadMoreMessages}
            className="flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors text-sm font-medium shadow-sm"
          >
            <span>Charger plus de messages</span>
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      )}
      
      <div className="flex justify-end mb-2">
        <button
          onClick={() => setSortNewestFirst(!sortNewestFirst)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          {sortNewestFirst ? (
            <>
              <ArrowDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span>Plus récents en haut</span>
            </>
          ) : (
            <>
              <ArrowUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span>Plus anciens en haut</span>
            </>
          )}
        </button>
      </div>

      {visibleMessages.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center p-6">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10 dark:from-primary-500/5 dark:to-accent-500/5 blur-2xl" />
            <div className="relative w-16 h-16 mb-4 bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-accent-900/30 rounded-2xl flex items-center justify-center transform transition-transform hover:scale-105">
              <MessageSquare className="w-8 h-8 text-primary-500 dark:text-primary-400" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Aucun message pour le moment
          </h3>
          <p className="text-base text-gray-600 dark:text-gray-400 max-w-sm">
            Soyez le premier à participer à la discussion et aidez la communauté à grandir !
          </p>
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200/50 dark:border-gray-700/50 max-w-sm">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Partagez votre expérience, posez vos questions ou donnez des conseils aux autres voyageurs.
            </p>
          </div>
        </div>
      ) : (
        visibleMessages.map(message => renderMessageWithReplies(message))
      )}

      {messageToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Confirmer la suppression
                </h3>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Êtes-vous sûr de vouloir supprimer ce message ? Cette action est irréversible.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setMessageToDelete(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMoveModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-4 sm:p-6 border-b dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <MoveRight className="w-5 h-5 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Déplacer message
                  </h3>
                </div>
                <button
                  onClick={() => setShowMoveModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Le message et ses réponses seront déplacés vers la section sélectionnée.
              </p>

              <div className="mb-6 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-accent-900/30 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                      {messageToMove?.username ? messageToMove.username[0].toUpperCase() : '?'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {messageToMove?.username}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {messageToMove?.created_at && formatDate(messageToMove.created_at.toDate())}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                      {messageToMove?.content}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Destination
                </label>
                <select
                  value={targetThread}
                  onChange={(e) => setTargetThread(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-shadow text-sm"
                  required
                >
                  <option value="">Sélectionner la destination</option>
                  <optgroup label="Général">
                    {availableThreads
                      .filter(t => t.type !== 'country')
                      .map(thread => (
                        <option 
                          key={thread.code} 
                          value={thread.code}
                          disabled={thread.code === messageToMove?.thread_code}
                        >
                          {thread.name}
                        </option>
                      ))}
                  </optgroup>
                  <optgroup label="Pays">
                    {availableThreads
                      .filter(t => t.type === 'country')
                      .map(thread => (
                        <option 
                          key={thread.code} 
                          value={thread.code}
                          disabled={thread.code === messageToMove?.thread_code}
                        >
                          {thread.name}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowMoveModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={executeMove}
                  disabled={!targetThread || isMoving}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 dark:disabled:bg-blue-700/50 rounded-lg transition-colors"
                >
                  {isMoving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Déplacement...</span>
                    </>
                  ) : (
                    <>
                      <MoveRight className="w-4 h-4" />
                      <span>Déplacer</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface MessageComponentProps {
  message: Message;
  isReply?: boolean;
  replyToMessage?: Message | null;
  isAdmin: boolean;
  userInfo: any;
  user: any;
  onDelete: () => void;
  onReply: () => void;
  onReaction: (messageId: string, isLike: boolean) => void;
  onReport: (messageId: string) => void;
  onMove: () => void;
}

function MessageComponent({ 
  message, 
  isReply = false, 
  replyToMessage,
  isAdmin,
  userInfo,
  user,
  onDelete, 
  onReply, 
  onReaction,
  onReport,
  onMove
}: MessageComponentProps) {
  const [showOptions, setShowOptions] = React.useState(false);
  const optionsRef = React.useRef<HTMLDivElement>(null);

  const isAdminMessage = React.useMemo(() => {
    return message.is_admin || message.user_id === 'admin' || 
           message.user_id === '1' || message.user_id === '357734' || message.user_id === '301079' || message.user_id === '387526' ||
           (message.user_id && userInfo?.claims?.some(claim => 
             claim.key === 'isAdmin' && claim.value === true && 
             message.user_id === user?.uid
           ));
  }, [message, userInfo, user]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target as Node)) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`group flex gap-3 ${isReply ? 'ml-8 mt-2' : ''}`}>
      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${
        isAdminMessage 
          ? 'bg-gradient-to-br from-primary-400 to-accent-400 ring-2 ring-white dark:ring-gray-800 shadow-lg' 
          : 'bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-accent-900/30'
      } flex items-center justify-center`}>
        {isAdminMessage ? (
          <span className="text-white text-sm font-bold">
            {message.username ? message.username[0].toUpperCase() : 'A'}
          </span>
        ) : (
          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
            {message.username ? message.username[0].toUpperCase() : '?'}
          </span>
        )}
      </div>

      <div className="flex-1 max-w-[85%] min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-sm font-medium ${
            isAdminMessage 
              ? 'text-primary-600 dark:text-primary-400 flex items-center gap-1.5 font-semibold' 
              : 'text-gray-900 dark:text-white'
          }`}>
            {message.username}
            {isAdminMessage && (
              <span className="inline-flex items-center px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-xs font-bold rounded-full border border-primary-200 dark:border-primary-800/30">
                Admin
              </span>
            )}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {formatDate(message.created_at.toDate())}
          </span>
        </div>

        <div className={`rounded-2xl px-4 py-2.5 ${
          isAdminMessage 
            ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg shadow-primary-500/10 dark:shadow-primary-500/5 border border-primary-400/20 dark:border-primary-400/10 relative' 
            : message.user_id === user?.uid
              ? 'bg-accent-500 text-white'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
        }`}>
          {isAdminMessage && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-sm border border-primary-200 dark:border-primary-700">
              <span className="text-xs text-primary-500 font-bold">★</span>
            </div>
          )}
          <p 
            className={`message-text whitespace-pre-wrap break-words ${isAdminMessage ? 'font-medium leading-relaxed' : ''}`}
            dangerouslySetInnerHTML={{
              __html: message.content.replace(
                /(https?:\/\/[^\s]+)/g,
                isAdminMessage 
                  ? '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-white underline hover:opacity-80 font-medium">$1</a>'
                  : '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">$1</a>'
              )
            }}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-1">
          {message.user_id !== user?.uid && (
            <>
              <button
                onClick={() => onReaction(message.id, true)}
                className={`flex items-center gap-1 px-2 py-1 text-sm rounded-full transition-colors ${
                  message.likes?.includes(user?.uid || '')
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
              >
                <ThumbsUp className="w-3.5 h-3.5" />
                {message.likes?.length > 0 && (
                  <span className="message-action">{message.likes.length}</span>
                )}
              </button>

              <button
                onClick={() => onReaction(message.id, false)}
                className={`flex items-center gap-1 px-2 py-1 text-sm rounded-full transition-colors ${
                  message.dislikes?.includes(user?.uid || '')
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-500 hover:text-red-600 dark:hover:text-red-400'
                }`}
              >
                <ThumbsDown className="w-3.5 h-3.5" />
                {message.dislikes?.length > 0 && (
                  <span className="message-action">{message.dislikes.length}</span>
                )}
              </button>
            </>
          )}

          <button
            onClick={onReply}
            className="flex items-center gap-1 px-2 py-1 message-action text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 rounded-full transition-colors"
          >
            <Reply className="w-3.5 h-3.5" />
            <span>Répondre</span>
          </button>
          
          <div ref={optionsRef} className="relative ml-auto">
            <button
              onClick={() => setShowOptions(!showOptions)}
              className="flex items-center gap-1 px-2 py-1 message-action text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-full transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="1" />
                <circle cx="19" cy="12" r="1" />
                <circle cx="5" cy="12" r="1" />
              </svg>
            </button>

            {showOptions && (
              <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10">
                {isAdmin && (
                  <>
                    <button
                      onClick={() => {
                        onDelete();
                        setShowOptions(false);
                      }}
                      className="w-full flex items-center gap-1.5 px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Supprimer</span>
                    </button>
                    <button
                      onClick={() => {
                        onMove();
                        setShowOptions(false);
                      }}
                      className="w-full flex items-center gap-1.5 px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <MoveRight className="w-4 h-4" />
                      <span>Déplacer</span>
                    </button>
                  </>
                )}
                {message.user_id !== user?.uid && (
                  <button
                    onClick={() => {
                      onReport(message.id);
                      setShowOptions(false);
                    }}
                    className="w-full flex items-center gap-1.5 px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Flag className="w-4 h-4" />
                    <span>Signaler</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}